import React, { useEffect, useRef, useState } from 'react';

interface BgVideoProps {
  /** MP4 URL. Current sources are Gumlet-hosted, so this is a full CDN URL. */
  src: string;
  /** Optional WebM alternative, served first when the browser takes it. */
  webm?: string;
  poster: string;
  label: string;
  /** Intrinsic pixel size, so the box is reserved before anything loads. */
  width: number;
  height: number;
  className?: string;
  videoClassName?: string;
  eager?: boolean;
}

/**
 * Ambient looping video that respects the viewer:
 * - Shows the poster frame until the card scrolls into view
 * - Never downloads video on small screens or with reduced motion
 * - Muted, inline, no controls. Pure atmosphere, no content.
 *
 * Video is delivered by Gumlet (CloudFront, originals kept at source
 * resolution). Do not point this at a hand-compressed local file: an earlier
 * pass squeezed these clips for bytes and the loss showed up as blur on
 * full-width backgrounds.
 *
 * Only the hero preloads; anything below the fold stays at preload="none" and
 * fetches nothing until it is scrolled into view.
 */
const BgVideo: React.FC<BgVideoProps> = ({
  src,
  webm,
  poster,
  label,
  width,
  height,
  className = '',
  videoClassName = '',
  eager = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.innerWidth < 768) return;
    const inView = () => {
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight + 200 && r.bottom > -200;
    };
    // Above the fold: no need to wait for the observer.
    if (inView()) {
      setActive(true);
      return;
    }
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setActive(true);
            io.disconnect();
          }
        },
        { rootMargin: '200px' }
      );
      io.observe(el);
      // Safety net: some embedded browsers never fire the observer.
      const t = setTimeout(() => {
        if (inView()) setActive(true);
      }, 4000);
      return () => {
        io.disconnect();
        clearTimeout(t);
      };
    }
    setActive(true);
  }, []);

  return (
    <div ref={ref} className={className} role="img" aria-label={label}>
      {active ? (
        <video
          className={videoClassName}
          poster={poster}
          width={width}
          height={height}
          autoPlay
          muted
          loop
          playsInline
          preload={eager ? 'auto' : 'none'}
        >
          {webm && <source src={webm} type="video/webm" />}
          <source src={src} type="video/mp4" />
        </video>
      ) : (
        <img
          src={poster}
          alt={label}
          width={width}
          height={height}
          loading="lazy"
          className={videoClassName}
        />
      )}
    </div>
  );
};

export default BgVideo;
